import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createFieldMapping,
  deleteFieldMapping,
  fetchFieldMappings,
  fetchFieldMappingsMeta,
  fetchUnmappedFields,
  remapIngestedData,
  updateFieldMapping,
  type FieldMapping,
  type RemapResponse,
  type UnmappedField,
} from '../../data/api';
import type { ConfirmIntent } from '../../components/ConfirmDialog';

const CARD = 'rounded-2xl border border-[#1e6bb824] bg-white p-4 shadow-sm md:p-5';
const INPUT = 'rounded-lg border border-[#1e6bb833] px-3 py-2';
const ERROR_BOX = 'mb-3 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]';
const OK_BOX = 'mb-3 rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 py-2 text-sm text-[#106b38]';
const WARN_BOX = 'mb-3 rounded-lg border border-[#e7d5b9] bg-[#fbf4e7] px-3 py-2 text-sm text-[#6b4710]';
const TH = 'px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-avcs-muted';

type Option = { value: string; label: string };
type Meta = { event_types: Option[]; target_fields: Option[] };

type Props = {
  /**
   * AdminHome's shared confirm dialog, so destructive actions match the rest
   * of admin. Typed as the full ConfirmIntent rather than just `{ message }`:
   * the narrow type meant this screen could not state a tone, so it inherited
   * AdminHome's old `danger` default and offered a red "Delete" for the
   * re-map, which deletes nothing.
   */
  requestConfirm: (options: ConfirmIntent) => Promise<boolean>;
};

const emptyDraft = { event_type: '', source_field: '', target_field: '', description: '' };

export default function FieldMappingsAdmin({ requestConfirm }: Props) {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [unmapped, setUnmapped] = useState<UnmappedField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filters, setFilters] = useState({ event_type: '', target_field: '', is_active: '', search: '' });
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [editDraft, setEditDraft] = useState<Partial<FieldMapping>>({});

  // Re-map panel
  const [remapEventType, setRemapEventType] = useState('');
  const [remapPlan, setRemapPlan] = useState<RemapResponse | null>(null);
  const [remapBusy, setRemapBusy] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<Record<string, boolean>>({});

  const eventTypeLabel = useCallback(
    (value: string) => meta?.event_types.find((o) => o.value === value)?.label || value,
    [meta],
  );

  const loadMappings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rows, unmappedRows] = await Promise.all([
        fetchFieldMappings(filters),
        fetchUnmappedFields(filters.event_type ? { event_type: filters.event_type } : {}),
      ]);
      setMappings(rows);
      setUnmapped(unmappedRows);
    } catch (e: any) {
      setError(e?.message || 'Failed to load field mappings.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchFieldMappingsMeta().then(setMeta).catch(() => setMeta(null));
  }, []);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  // -- mapping CRUD ---------------------------------------------------------
  const submitDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.event_type || !draft.source_field.trim() || !draft.target_field) {
      setError('Event type, source field and target field are all required.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await createFieldMapping({
        event_type: draft.event_type,
        source_field: draft.source_field.trim(),
        target_field: draft.target_field,
      });
      setDraft(emptyDraft);
      setSuccess('Mapping created. It applies to future uploads — use “Re-apply to existing data” below to fix rows already ingested.');
      await loadMappings();
    } catch (e: any) {
      setError(e?.message || 'Could not create the mapping.');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id: string) => {
    setError('');
    setSuccess('');
    try {
      const updated = await updateFieldMapping(id, editDraft);
      setMappings((prev) => prev.map((m) => (m.id === id ? updated : m)));
      setEditingId('');
      setEditDraft({});
      setSuccess('Mapping updated. Existing rows keep their old values until you re-apply below.');
    } catch (e: any) {
      setError(e?.message || 'Could not save the mapping.');
    }
  };

  const toggleActive = async (mapping: FieldMapping) => {
    setError('');
    try {
      const updated = await updateFieldMapping(mapping.id, { is_active: !mapping.is_active });
      setMappings((prev) => prev.map((m) => (m.id === mapping.id ? updated : m)));
    } catch (e: any) {
      setError(e?.message || 'Could not change the mapping.');
    }
  };

  const remove = async (mapping: FieldMapping) => {
    const confirmed = await requestConfirm({
      title: 'Delete mapping',
      message:
        `Delete the mapping “${mapping.source_field}” → ${mapping.target_field}? ` +
        'Data already ingested keeps the values this mapping produced. ' +
        'To stop using it while keeping the record, deactivate it instead.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!confirmed) return;
    setError('');
    try {
      await deleteFieldMapping(mapping.id);
      setMappings((prev) => prev.filter((m) => m.id !== mapping.id));
      setSuccess('Mapping deleted.');
    } catch (e: any) {
      setError(e?.message || 'Could not delete the mapping.');
    }
  };

  const mapUnmapped = async (field: UnmappedField, targetField: string) => {
    if (!targetField) return;
    setError('');
    setSuccess('');
    try {
      await createFieldMapping({
        event_type: field.event_type,
        source_field: field.source_field,
        target_field: targetField,
      });
      setSuccess(`“${field.source_field}” now maps to ${targetField}.`);
      await loadMappings();
    } catch (e: any) {
      setError(e?.message || 'Could not create the mapping.');
    }
  };

  // -- re-map ---------------------------------------------------------------
  const runDryRun = async () => {
    if (!remapEventType) {
      setError('Choose an event type to scan.');
      return;
    }
    setRemapBusy(true);
    setError('');
    setSuccess('');
    setRemapPlan(null);
    setSelectedSessions({});
    try {
      const plan = await remapIngestedData({ event_type: remapEventType });
      setRemapPlan(plan);
      // Pre-select only the sessions that would actually change.
      setSelectedSessions(
        Object.fromEntries(plan.results.filter((r) => r.rows_changed > 0).map((r) => [r.session_id, true])),
      );
    } catch (e: any) {
      setError(e?.message || 'Could not run the dry run.');
    } finally {
      setRemapBusy(false);
    }
  };

  const chosenSessionIds = useMemo(
    () => Object.entries(selectedSessions).filter(([, on]) => on).map(([id]) => id),
    [selectedSessions],
  );

  const applyRemap = async () => {
    if (!chosenSessionIds.length) return;
    const affected = (remapPlan?.results || []).filter((r) => chosenSessionIds.includes(r.session_id));
    const rows = affected.reduce((sum, r) => sum + r.rows_changed, 0);
    const confirmed = await requestConfirm({
      title: 'Re-map ingested data',
      message:
        `Re-map ${rows} row(s) across ${affected.length} upload(s)?\n\n` +
        'Each upload is rewritten from its original file and then re-linked, which can change ' +
        'which animal a row resolves to and the event counts derived from it. ' +
        'The original uploaded data is not modified.',
      confirmLabel: 'Re-map',
      tone: 'danger',
    });
    if (!confirmed) return;

    setRemapBusy(true);
    setError('');
    try {
      const applied = await remapIngestedData({ session_ids: chosenSessionIds, apply: true });
      setRemapPlan(applied);
      setSuccess(`Re-mapped ${applied.totals.rows_written ?? 0} row(s) across ${chosenSessionIds.length} upload(s).`);
    } catch (e: any) {
      setError(e?.message || 'Re-map failed.');
    } finally {
      setRemapBusy(false);
    }
  };

  const targetFields = meta?.target_fields ?? [];
  const eventTypes = meta?.event_types ?? [];

  return (
    <div className="flex flex-col gap-4">
      <section className={CARD}>
        <div className="admin-section-head mb-2 flex items-center justify-between gap-2">
          <h2 className="text-2xl font-semibold text-avcs-text">Ingest field mappings</h2>
        </div>
        <p className="mb-3 text-xs text-avcs-muted">
          How a column heading in an uploaded file becomes a database field, per event type. Some headings are
          recognised automatically; anything else needs a mapping here. Mappings apply when a file is
          <strong> read</strong>, so a correction changes future uploads only — use <strong>Re-apply to existing data</strong>
          {' '}below to fix rows that were already ingested wrongly.
        </p>

        {error && <div className={ERROR_BOX}>{error}</div>}
        {success && <div className={OK_BOX}>{success}</div>}

        <form className="mb-4 grid gap-3 rounded-xl border border-[#1e6bb824] bg-[#fbfeff] p-3 md:grid-cols-4" onSubmit={submitDraft}>
          <label className="grid gap-1 text-sm text-avcs-text">
            Event type *
            <select className={INPUT} value={draft.event_type} onChange={(e) => setDraft((d) => ({ ...d, event_type: e.target.value }))}>
              <option value="">Select…</option>
              {eventTypes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-sm text-avcs-text">
            Column in the file *
            <input className={INPUT} value={draft.source_field} placeholder="e.g. Massa" onChange={(e) => setDraft((d) => ({ ...d, source_field: e.target.value }))} />
          </label>
          <label className="grid gap-1 text-sm text-avcs-text">
            Database field *
            <select className={INPUT} value={draft.target_field} onChange={(e) => setDraft((d) => ({ ...d, target_field: e.target.value }))}>
              <option value="">Select…</option>
              {targetFields.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <div className="flex items-end">
            <button className="btn admin-submit-action" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add mapping'}</button>
          </div>
        </form>

        <div className="mb-3 grid gap-3 md:grid-cols-4">
          <select className={INPUT} value={filters.event_type} onChange={(e) => setFilters((f) => ({ ...f, event_type: e.target.value }))}>
            <option value="">All event types</option>
            {eventTypes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className={INPUT} value={filters.target_field} onChange={(e) => setFilters((f) => ({ ...f, target_field: e.target.value }))}>
            <option value="">All database fields</option>
            {targetFields.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className={INPUT} value={filters.is_active} onChange={(e) => setFilters((f) => ({ ...f, is_active: e.target.value }))}>
            <option value="">Active and inactive</option>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </select>
          <input className={INPUT} value={filters.search} placeholder="Search…" onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
        </div>

        {loading && <p className="text-sm text-avcs-muted">Loading…</p>}

        <div className="admin-table-scroll">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[#1e6bb824] bg-[#f8fbff]">
              <tr>
                <th className={TH}>Event type</th>
                <th className={TH}>Column in the file</th>
                <th className={TH}>Database field</th>
                <th className={TH}>Active</th>
                <th className={TH}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1e6bb812]">
              {!loading && mappings.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-avcs-muted">No mappings match these filters.</td></tr>
              )}
              {mappings.map((m) => {
                const editing = editingId === m.id;
                return (
                  <tr key={m.id} className={m.is_active ? 'bg-white hover:bg-[#f8fbff]' : 'bg-[#fafafa] text-avcs-muted hover:bg-[#f4f4f4]'}>
                    <td className="px-4 py-2">{eventTypeLabel(m.event_type)}</td>
                    <td className="px-4 py-2">
                      {editing ? (
                        <input className={`${INPUT} w-full`} defaultValue={m.source_field} onChange={(e) => setEditDraft((d) => ({ ...d, source_field: e.target.value }))} />
                      ) : (
                        <span className="font-medium text-avcs-text">{m.source_field}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {editing ? (
                        <select className={`${INPUT} w-full`} defaultValue={m.target_field} onChange={(e) => setEditDraft((d) => ({ ...d, target_field: e.target.value }))}>
                          {targetFields.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : m.target_field}
                    </td>
                    <td className="px-4 py-2">{m.is_active ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-2">
                        {editing ? (
                          <>
                            <button className="btn" onClick={() => saveEdit(m.id)}>Save</button>
                            <button className="btn" onClick={() => { setEditingId(''); setEditDraft({}); }}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button className="btn" onClick={() => { setEditingId(m.id); setEditDraft({}); }}>Edit</button>
                            <button className="btn" onClick={() => toggleActive(m)}>{m.is_active ? 'Deactivate' : 'Activate'}</button>
                            <button className="btn admin-danger-action" onClick={() => remove(m)}>Delete</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className={CARD}>
        <h3 className="mb-2 text-xl font-semibold text-avcs-text">Unrecognised columns</h3>
        <p className="mb-3 text-xs text-avcs-muted">
          Columns seen in uploads that no rule matched, so their values were dropped. Map one and it will be read
          from the next upload onwards.
        </p>
        <div className="admin-table-scroll">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[#1e6bb824] bg-[#f8fbff]">
              <tr>
                <th className={TH}>Event type</th>
                <th className={TH}>Column</th>
                <th className={TH}>Example value</th>
                <th className={TH}>Times seen</th>
                <th className={TH}>Map to</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1e6bb812]">
              {unmapped.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-avcs-muted">Nothing unrecognised.</td></tr>
              )}
              {unmapped.map((f) => (
                <tr key={f.id} className="bg-white hover:bg-[#f8fbff]">
                  <td className="px-4 py-2">{eventTypeLabel(f.event_type)}</td>
                  <td className="px-4 py-2 font-medium text-avcs-text">{f.source_field}</td>
                  <td className="px-4 py-2 text-avcs-muted">{f.sample_value || '—'}</td>
                  <td className="px-4 py-2">{f.occurrence_count}</td>
                  <td className="px-4 py-2">
                    <select className={INPUT} defaultValue="" onChange={(e) => mapUnmapped(f, e.target.value)}>
                      <option value="">Choose a field…</option>
                      {targetFields.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={CARD}>
        <h3 className="mb-2 text-xl font-semibold text-avcs-text">Re-apply to existing data</h3>
        <p className="mb-3 text-xs text-avcs-muted">
          Rebuilds already-ingested rows from their original uploaded files using the mappings above. The dry run
          writes nothing — review what it reports, then choose which uploads to fix.
        </p>

        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="grid gap-1 text-sm text-avcs-text">
            Event type
            <select className={INPUT} value={remapEventType} onChange={(e) => setRemapEventType(e.target.value)}>
              <option value="">Select…</option>
              {eventTypes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <button className="btn" onClick={runDryRun} disabled={remapBusy || !remapEventType}>
            {remapBusy ? 'Working…' : 'Dry run'}
          </button>
          {remapPlan && !remapPlan.apply && (
            <button className="btn admin-submit-action" onClick={applyRemap} disabled={remapBusy || chosenSessionIds.length === 0}>
              Apply to {chosenSessionIds.length} selected upload(s)
            </button>
          )}
        </div>

        {remapPlan && (
          <>
            {remapPlan.sessions_truncated && (
              <div className={WARN_BOX}>
                Showing {remapPlan.sessions_scanned} of {remapPlan.sessions_matched} uploads (limit {remapPlan.session_limit}).
                Fix these first, then run again to see the rest.
              </div>
            )}
            <p className="mb-2 text-sm text-avcs-text">
              {remapPlan.apply
                ? `Applied: ${remapPlan.totals.rows_written ?? 0} row(s) rewritten.`
                : `${remapPlan.totals.rows_changed} of ${remapPlan.totals.rows_total} row(s) would change across ${remapPlan.sessions_scanned} upload(s).`}
            </p>

            <div className="admin-table-scroll">
              <table className="min-w-full text-sm">
                <thead className="border-b border-[#1e6bb824] bg-[#f8fbff]">
                  <tr>
                    <th className={TH}>{remapPlan.apply ? '' : 'Fix'}</th>
                    <th className={TH}>Upload</th>
                    <th className={TH}>Deal</th>
                    <th className={TH}>Rows changed</th>
                    <th className={TH}>Fields</th>
                    <th className={TH}>Example</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e6bb812]">
                  {remapPlan.results.map((r) => (
                    <tr key={r.session_id} className={r.rows_changed ? 'bg-white' : 'bg-[#fafafa] text-avcs-muted'}>
                      <td className="px-4 py-2">
                        {!remapPlan.apply && r.rows_changed > 0 && (
                          <input
                            type="checkbox"
                            checked={!!selectedSessions[r.session_id]}
                            onChange={(e) => setSelectedSessions((s) => ({ ...s, [r.session_id]: e.target.checked }))}
                          />
                        )}
                      </td>
                      <td className="px-4 py-2 font-medium text-avcs-text">{r.session_label}</td>
                      <td className="px-4 py-2">{r.deal_no || '—'}</td>
                      <td className="px-4 py-2">{r.rows_changed} / {r.rows_total}</td>
                      <td className="px-4 py-2">
                        {Object.entries(r.fields).map(([field, counts]) => (
                          <span key={field} className="mr-2 whitespace-nowrap">
                            <strong>{field}</strong>{' '}
                            {counts.gained ? `+${counts.gained} ` : ''}
                            {counts.changed ? `~${counts.changed} ` : ''}
                            {counts.lost ? `-${counts.lost}` : ''}
                          </span>
                        ))}
                        {Object.keys(r.fields).length === 0 && '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-avcs-muted">
                        {r.sample
                          ? r.sample.fields.map((f) => (
                              <div key={f}>
                                row {r.sample!.row_number}: {f} {JSON.stringify(r.sample!.before[f])} → {JSON.stringify(r.sample!.after[f])}
                              </div>
                            ))
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
